import { useEffect, useState, type ReactNode } from "react";
import { observer } from "mobx-react-lite";
import { Cpu, MemoryStick, ArrowDownUp, HardDrive, Maximize2 } from "lucide-react";
import { socketStore } from "../../stores/socket.store";
import { serverStore } from "../../stores/server.store";
import { userStore } from "../../stores/user.store";
import { cn } from "../../utils/cn";
import { canAny } from '@/utils/permissions';

interface GlobalStats {
  cpu: number;
  ram: { total: number; used: number; free: number };
  disk: { total: number; used: number };
  disks?: { mount: string; total: number; used: number; use: number }[];
  network?: { rx_sec: number; tx_sec: number };
}

const fmtBytes = (b: number) => {
  if (!b) return "0 GB";
  const gb = b / 1024 ** 3;
  if (gb >= 1000) return (gb / 1024).toFixed(1) + " TB";
  return gb.toFixed(gb < 10 ? 1 : 0) + " GB";
};
const fmtSpeed = (b: number) => {
  if (!b) return "0 KB/s";
  const k = 1024;
  const units = ["B/s", "KB/s", "MB/s", "GB/s"];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return (b / k ** i).toFixed(1) + " " + units[i];
};
const diskLabel = (mount: string) => (mount === "/" ? "System" : mount.split("/").filter(Boolean).pop() || mount);
const barColor = (pct: number) => (pct > 90 ? "bg-red-500" : pct > 75 ? "bg-warn" : "bg-ember");

const Metric = ({ icon, label, value, pct, sub }: { icon: ReactNode; label: string; value: string; pct?: number; sub?: ReactNode }) => (
  <div className="min-w-0">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-fg-muted">
      <span className="text-fg-muted/80">{icon}</span>
      <span className="truncate">{label}</span>
    </div>
    <div className="mt-1 font-mono text-sm text-fg truncate">{value}</div>
    {pct !== undefined ? (
      <div className="mt-1.5 h-1 rounded-full bg-white/[0.06] overflow-hidden">
        <div className={cn("h-full rounded-full transition-all duration-500", barColor(pct))} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
      </div>
    ) : (
      sub && <div className="mt-1 text-[11px]">{sub}</div>
    )}
  </div>
);

export const SystemBar = observer(() => {
  const [stats, setStats] = useState<GlobalStats | null>(null);
  const cu = userStore.currentUser;
  const canOpen = canAny(cu, ["can_manage_system", "can_root_reboot"]);

  useEffect(() => {
    // Seed immediately so the bar is never stuck on "waiting" — the socket then
    // keeps it live (and adds network throughput).
    let seeded = false;
    serverStore.fetchSystemInfo().then((info: any) => {
      if (seeded || !info) return;
      const root = info.disk?.find((d: any) => d.mount === "/") || info.disk?.[0];
      setStats((prev) =>
        prev ?? {
          cpu: info.load?.currentLoad ?? 0,
          // "used" from system info counts reclaimable cache; total − available is
          // the real footprint (and matches what the live socket reports).
          ram: {
            total: info.mem?.total ?? 0,
            used: Math.max(0, (info.mem?.total ?? 0) - (info.mem?.available ?? info.mem?.free ?? 0)),
            free: info.mem?.available ?? 0,
          },
          disk: { total: root?.size ?? 0, used: root?.used ?? 0 },
          disks: (info.disk || [])
            .filter((d: any) => !String(d.mount).includes("/snap") && !String(d.fs).includes("loop"))
            .map((d: any) => ({ mount: d.mount, total: d.size, used: d.used, use: d.size ? (d.used / d.size) * 100 : 0 })),
          network: { rx_sec: 0, tx_sec: 0 },
        }
      );
    });

    const socket = socketStore.rawSocket;
    if (!socket) return () => { seeded = true; };
    const onStats = (data: GlobalStats) => { seeded = true; setStats(data); };
    socket.on("global_stats", onStats);
    return () => { seeded = true; socket.off("global_stats", onStats); };
  }, []);

  const ramPct = stats?.ram.total ? (stats.ram.used / stats.ram.total) * 100 : 0;
  const disks = stats?.disks?.length ? stats.disks : stats ? [{ mount: "/", total: stats.disk.total, used: stats.disk.used, use: stats.disk.total ? (stats.disk.used / stats.disk.total) * 100 : 0 }] : [];

  return (
    <div className="bg-surface border border-line rounded-2xl p-4 sm:p-5">
      <div className="flex items-center justify-between mb-3.5">
        <h2 className="text-xs font-medium text-fg-muted uppercase tracking-wider flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-ember gm-live-dot" /> System-Ressourcen
        </h2>
        {canOpen && (
          <button
            onClick={() => (serverStore.showSystemDetails = true)}
            className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-ember transition-colors"
          >
            <Maximize2 size={13} /> Details
          </button>
        )}
      </div>

      {!stats ? (
        <div className="text-xs text-fg-muted/70 font-mono py-2">Warte auf Live-Daten…</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-x-6 gap-y-4">
          <Metric icon={<Cpu size={12} />} label="CPU" value={`${Math.round(stats.cpu)}%`} pct={stats.cpu} />
          <Metric icon={<MemoryStick size={12} />} label="RAM" value={`${fmtBytes(stats.ram.used)} / ${fmtBytes(stats.ram.total)}`} pct={ramPct} />
          <Metric
            icon={<ArrowDownUp size={12} />}
            label="Netzwerk"
            value={`↓ ${fmtSpeed(stats.network?.rx_sec || 0)}`}
            sub={<span className="font-mono text-fg-muted">↑ {fmtSpeed(stats.network?.tx_sec || 0)}</span>}
          />
          {disks.map((d) => (
            <Metric
              key={d.mount}
              icon={<HardDrive size={12} />}
              label={diskLabel(d.mount)}
              value={`${fmtBytes(d.used)} / ${fmtBytes(d.total)}`}
              pct={d.use ?? (d.total ? (d.used / d.total) * 100 : 0)}
            />
          ))}
        </div>
      )}
    </div>
  );
});
